import { S } from '../core/game';
import { bus } from '../core/bus';
import { m } from '../i18n';
import { sfx } from '../audio/sfx';
import { CHORES } from '../core/state';
import { CLOVERS, canStart, cloversFound, isActive, isDone, questList, questText, step, type QuestId } from '../content/quests';
import { CHAPTERS, chapterProgress } from '../content/story';
import { todaysObjectives } from '../content/days';
import { h, type Child } from './dom';
import { isPanelOpen, openPanel, refreshPanel } from './panel';

type Tab = 'today' | 'quests';
let tab: Tab = 'today';

/** The journal: today's goals and chores on one page, the quest log on the other. */
export function openQuestbook(start?: Tab): void {
  if (start) tab = start;
  openPanel('journal', () => {
    const s = S();
    const t = m();
    const list = questList(s);
    const offers = list.filter((q) => q.state === 'available').length;
    const open = todaysObjectives(s).filter((o) => !o.done).length;
    const tabs = h(
      'div',
      { class: 'journal-tabs' },
      tabBtn('today', `📋 ${t.objectives.title}${open ? ` · ${open}` : ''}`),
      tabBtn('quests', `📜 ${t.quests.title}${offers ? ` · ${offers}` : ''}`),
    );
    return {
      title: `📔 ${t.side.journal}`,
      body: [tabs, tab === 'today' ? today() : quests()],
    };
  });
}

/** Kept for callers that still open the quest log directly. */
export function openQuests(): void {
  openQuestbook('quests');
}

function tabBtn(id: Tab, label: string): HTMLElement {
  return h(
    'button',
    {
      class: tab === id ? 'on' : '',
      onclick: () => {
        sfx('click');
        tab = id;
        refreshPanel('journal');
      },
    },
    label,
  );
}

function check(done: boolean, label: string): HTMLElement {
  return h('div', { class: `check${done ? ' done' : ''}` }, h('span', { class: 'box' }, done ? '✓' : ''), h('span', null, label));
}

function today(): Child {
  const s = S();
  const c = m().chores;
  return [
    h('div', { class: 'checklist' }, todaysObjectives(s).map((o) => check(o.done, o.text))),
    h('h4', null, `🏠 ${c.title}`),
    h('p', { class: 'hint' }, c.hint),
    h('div', { class: 'checklist' }, CHORES.map((k) => check(!!s.chores[k], c[k]))),
  ];
}

function quests(): Child {
  const t = m();
  const s = S();
  return CHAPTERS.map((ch, i) => {
    const prog = chapterProgress(ch, s);
    const text = t.story.chapters[ch.id];
    const anyOpen = ch.quests.some((q) => isActive(q) || canStart(q));
    const cards = ch.quests.map((q) => {
      const state = isDone(q) ? 'done' : isActive(q) ? 'active' : canStart(q) ? 'available' : 'locked';
      return questCard(q, state);
    });
    return h(
      'div',
      { class: `chapter${!anyOpen && prog.done === 0 ? ' locked' : ''}` },
      h(
        'div',
        { class: 'chapter-head' },
        h('span', { class: 'ico' }, ch.icon),
        h('div', { class: 'ttl' }, h('small', null, t.story.ui.chapter(i + 1)), h('b', null, text.title)),
        h('span', { class: 'prog' }, t.story.ui.progress(prog.done, prog.total)),
      ),
      h('div', { class: 'chapter-bar' }, h('i', { style: `width:${(prog.done / prog.total) * 100}%` })),
      h('p', { class: 'chapter-hint' }, text.hint),
      cards,
    );
  });
}

function questCard(id: QuestId, state: 'active' | 'available' | 'done' | 'locked'): HTMLElement {
  const t = m().quests;
  const s = S();
  const q = questText(id);
  const st = Math.max(0, step(id));
  if (state === 'locked') return h('div', { class: 'quest done' }, h('div', { class: 'quest-head' }, h('b', null, q.title), h('span', { class: 'badge-available' }, m().story.ui.locked)));
  const hint = state === 'done' ? t.done : state === 'available' ? q.steps[0] : q.steps[Math.min(st, q.steps.length - 1)];
  const tracked = s.tracked === id;
  const extra = id === 'clovers' ? ` (${cloversFound(s)}/${CLOVERS.length})` : '';
  const steps = q.steps.map((_, i) => h('i', { class: state === 'done' || i < st ? 'done' : state === 'active' && i === st ? 'on' : '' }));
  return h(
    'div',
    { class: `quest ${state}${tracked ? ' tracked' : ''}` },
    h('div', { class: 'quest-head' }, h('b', null, `${q.title}${extra}`), h('span', { class: `badge-${state}` }, t[state])),
    h('p', { class: 'quest-desc' }, q.desc),
    state === 'available' ? null : h('div', { class: 'quest-steps' }, steps),
    h('p', { class: 'quest-step' }, h('span', null, '➜'), h('span', null, hint)),
    state === 'active'
      ? h(
          'button',
          {
            class: `btn small ${tracked ? 'gold' : 'ghost'}`,
            onclick: () => {
              sfx('click');
              s.tracked = tracked ? null : id;
              bus.emit('change');
            },
          },
          tracked ? `🧭 ${t.tracking}` : `🧭 ${t.track}`,
        )
      : null,
  );
}

bus.on('change', () => {
  if (isPanelOpen('journal')) refreshPanel('journal');
});
